import { Storage } from 'aws-amplify';
import { Document, SharedDocument } from './types/document';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import { ProfilePicture } from './types/deprecated/profile';
import { Address } from './types/types';
import { VerificationStatus } from './types/verification';
import { SharedDocumentsRequest } from './types/sharedDocuments';
import JSZip from 'jszip';
import mimetypes from './assets/mimetypes.json';
import { useAppSelector } from './redux/store';
import { RoleName, User, UserGroup } from './types/user';
import { styled } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
dayjs.extend(relativeTime);
dayjs.extend(duration);

// Build-time flags live in the dependency-free `./config/build` module so they
// can be imported by low-level code (i18n bootstrap, locale helpers) without
// pulling in this module's Redux/MUI/aws-amplify side effects. Re-exported here
// so existing `import { corporateWalletFlag } from './utils'` callers keep working.
export { corporateWalletFlag, cssBackgroundFlag } from './config/build';

export const getProcessFile =
	({ customHash, customName }: { customHash?: string; customName?: string }) =>
	async ({ file }: any) => {
		const fileExtension = file.name.split('.').pop();

		return file
			.arrayBuffer()
			.then((filebuffer: any) => window.crypto.subtle.digest('SHA-256', filebuffer))
			.then((hashBuffer: any) => {
				const hashArray = Array.from(new Uint8Array(hashBuffer));
				const hashHex = hashArray.map((a) => a.toString(16).padStart(2, '0')).join('');
				const metadata = {
					fileName: file.name,
					hash: customHash || hashHex
				};
				return {
					file,
					key: `${customName || customHash || hashHex}.${fileExtension}`,
					metadata
				};
			});
	};

export const processFile = async (file: any) => {
	const fileExtension = file.name.split('.').pop();

	return file
		.arrayBuffer()
		.then((filebuffer: any) => window.crypto.subtle.digest('SHA-256', filebuffer))
		.then((hashBuffer: any) => {
			const hashArray = Array.from(new Uint8Array(hashBuffer));
			const hashHex = hashArray.map((a) => a.toString(16).padStart(2, '0')).join('') + Date.now();
			const metadata = {
				fileName: file.name,
				hash: hashHex
			};
			return {
				file,
				key: `${hashHex}.${fileExtension}`,
				metadata
			};
		});
};

export const processFileVerificationRequests = async (file: any, prefix = '') => {
	const fileExtension = file.name.split('.').pop();

	return file
		.arrayBuffer()
		.then((filebuffer: any) => window.crypto.subtle.digest('SHA-256', filebuffer))
		.then((hashBuffer: any) => {
			const hashArray = Array.from(new Uint8Array(hashBuffer));
			const hashHex = hashArray.map((a) => a.toString(16).padStart(2, '0')).join('') + Date.now();

			const metadata = {
				fileName: file.name,
				hash: hashHex
			};

			return {
				file,
				key: `${prefix}${hashHex}.${fileExtension}`,
				metadata
			};
		});
};

// donwloads the blob
export function downloadBlob(blob: Blob, filename: string) {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename || 'download';
	const clickHandler = () => {
		setTimeout(() => {
			URL.revokeObjectURL(url);
			a.removeEventListener('click', clickHandler);
		}, 150);
	};
	a.addEventListener('click', clickHandler, false);
	a.click();
	return a;
}

export const getDocumentBlob = async (doc: Document) => {
	const { key, level, identityId } = doc;
	const result = await Storage.get(key, {
		download: true,
		level,
		identityId
	});
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	return result.Body;
};

// retrieves the document blob and then downloads it
export const downloadDocument = async (doc: Document, name?: string) => {
	const { title } = doc;
	const blob = await getDocumentBlob(doc);

	if (!blob) return;
	downloadBlob(blob, name ?? title);
};

export const openDocument = (document: Document) => {
	Storage.get(document.key ?? '', {
		level: document.level,
		identityId: document.identityId
	}).then((url) => window.open(url, '_blank'));
};

// donwloads a url
export function downloadUrl(url: string, filename: string) {
	const a = document.createElement('a');
	a.href = url;
	a.download = filename || 'download';
	const clickHandler = () => {
		setTimeout(() => {
			URL.revokeObjectURL(url);
			a.removeEventListener('click', clickHandler);
		}, 150);
	};
	a.addEventListener('click', clickHandler, false);
	a.click();
	return a;
}

export const downloadDocumentsZip = async (documents: { document: Document; name: string }[], folderName: string) => {
	const zip = new JSZip();
	for (const document of documents) {
		const blob = await getDocumentBlob(document.document);
		if (!blob) continue;
		const mimetype = document.document.mimetype;
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		let extension = mimetypes[mimetype].extensions[0];
		if (extension) {
			extension = '.' + extension;
		} else {
			extension = '';
		}
		const name = document.name + extension;
		zip.file(name, blob);
	}
	zip.generateAsync({ type: 'blob' }).then((content) => {
		downloadBlob(content, folderName);
	});
};

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export function formatFileSize(bytes: number, si = true, dp = 1) {
	const thresh = si ? 1000 : 1024;

	if (Math.abs(bytes) < thresh) {
		return bytes + ' B';
	}

	const units = si
		? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
		: ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
	let u = -1;
	const r = 10 ** dp;

	do {
		bytes /= thresh;
		++u;
	} while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

	return bytes.toFixed(dp) + ' ' + units[u];
}

export function getDocumentType(document: { mimetype: string }) {
	switch (document.mimetype) {
		case 'image/png':
			return 'png';
		case 'image/jpg':
		case 'image/jpeg':
			return 'jpg';
		case 'application/pdf':
			return 'pdf';
		case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
		case 'application/msword':
			return 'doc';
	}
	return 'blank';
}

export function getDocumentPreviewImage(document: Document) {
	const type = getDocumentType(document);
	switch (type) {
		case 'pdf':
			return {
				key: document.key + '/preview.png',
				level: document.level,
				identityId: document.identityId
			};
		case 'jpg':
		case 'png':
			return {
				key: document.key,
				level: document.level,
				identityId: document.identityId
			};
	}
	return undefined;
}

export function formatDate(date?: string | Date, format?: string) {
	if (!date) return '-';
	const defaultFormat = 'YYYY/MMM/DD';
	return dayjs(date).format(format ?? defaultFormat);
}

export function formatDateTime(date?: string | Date, format?: string) {
	if (!date) return '-';
	const defaultFormat = 'YYYY/MMM/DD - HH:mm:ss';
	return dayjs(date).format(format ?? defaultFormat);
}

export async function getProfilePictureUrl(picture: ProfilePicture) {
	if (picture?.type === 'custom') {
		// retrieve profile pictureˆ
		const href = await Storage.get(picture.key ?? '', {
			level: picture.level,
			identityId: picture.identityId
		});
		return href;
	}
	if (picture?.type === 'avatar') {
		try {
			const href = require(`/src/assets/images/avatars/${picture.path}`);
			return href;
		} catch {
			return null;
		}
	}
	return null;
}

export function isVerified(document?: Document | SharedDocument) {
	return Boolean(document?.verification?.status === VerificationStatus.APPROVED);
}

export function stringifyAddress(address?: Address) {
	if (!address) return undefined;
	return Object.values(address)
		.filter((value) => (value ?? '').trim().length)
		.join(', ');
}

export function getUserFullName(
	user: { firstName?: string; lastName?: string; email?: string; serialNumber?: string },
	options?: { serialNumber?: boolean }
) {
	const name = [user.firstName, user.lastName].filter((name) => name?.trim().length);
	if (options?.serialNumber && user.serialNumber?.trim().length) {
		name.unshift(user.serialNumber);
	}
	if (!name.length) return user.email ?? '-';
	return name.join(' ');
}

export function getSharedDocumentsRequestProgress(request?: SharedDocumentsRequest) {
	if (!request || !request.steps) return '';
	const mandatorySteps = new Set<number>();
	request.steps.forEach((step, number) => {
		if (!step.optional) mandatorySteps.add(number);
	});
	const total = mandatorySteps.size;
	request.stepsData?.forEach(({ stepNumber }) => mandatorySteps.delete(stepNumber));
	const completed = total - mandatorySteps.size;
	// return percentage
	return completed + '/' + total;
}

export function getSharedDocumentsRequestExpirationDateTime(request?: SharedDocumentsRequest) {
	if (!request) return '';
	if (!request.expirationDateTime) return '';
	return dayjs(request.expirationDateTime).format('YYYY/MMM/DD');
}

export function getDateRangeInDays(startDate: string) {
	const startDateParsed = dayjs(startDate);
	const today = dayjs();
	const startDateDiff = startDateParsed.diff(today, 'day');
	if (startDateDiff <= 1) return '1-7 days';
	if (startDateDiff <= 8) return '8-14 days';
	if (startDateDiff < 30) return '14-30 days';
	return '30+ days';
}

export function getDateRangeForDocumentRequest() {
	const today = dayjs();
	const ranges: any = { firstRange: [], secondRange: [], thirdRange: [] };

	ranges.firstRange.push(today.clone().format('YYYY-MM-DD'));
	ranges.firstRange.push(today.clone().add(8, 'days').format('YYYY-MM-DD'));
	ranges.secondRange.push(today.clone().add(8, 'days').format('YYYY-MM-DD'));
	ranges.secondRange.push(today.clone().add(16, 'days').format('YYYY-MM-DD'));
	ranges.thirdRange.push(today.clone().add(31, 'days').format('YYYY-MM-DD'));
	ranges.thirdRange.push(today.clone().add(1, 'years').format('YYYY-MM-DD'));

	return ranges;
}

export function isSharedDocumentsRequestPending(request?: SharedDocumentsRequest) {
	if (!request) return '';
	const mandatorySteps = new Set<number>();
	request.steps.forEach((step, number) => {
		if (!step.optional) mandatorySteps.add(number);
	});
	const total = mandatorySteps.size;
	request.stepsData?.forEach(({ stepNumber }) => mandatorySteps.delete(stepNumber));
	const completed = total - mandatorySteps.size;
	// return percentage
	return completed !== total;
}

export function dueDateCountdown(request?: any) {
	if (!request.expirationDateTime) return '';
	const now = dayjs();
	const expiration = dayjs(request.expirationDateTime);
	const daysDifference = expiration.diff(now, 'day');

	if (daysDifference < 0) {
		return 'Overdue';
	} else if (daysDifference === 0) {
		return 'Today';
	} else if (daysDifference === 1) {
		return '1 day';
	} else if (daysDifference < 7) {
		return `${daysDifference} days`;
	}
}

export function isExpiringSoon(request?: any) {
	if (!request.expirationDateTime) return '';
	const diff = dayjs(request.expirationDateTime).diff(dayjs(), 'day');
	return diff < 7;
}

export const isRoleCompany = () => {
	const organisationType = useAppSelector((state) => state.organisation.current?.type);
	const isPearsana = useAppSelector((state) => state.organisation.current?.isPearsana);

	return organisationType === 'company' || isPearsana === true;
};

export const isRoleEducational = () => {
	const organisationType = useAppSelector((state) => state.organisation.current?.type);
	const isPearsana = useAppSelector((state) => state.organisation.current?.isPearsana);

	return organisationType === 'educationalInstitution' || isPearsana === true;
};

export const loadApp = async (time = 1000) => {
	const promise = new Promise<void>((resolve) => {
		setTimeout(() => {
			resolve();
		}, time);
	});

	await promise;
};

type StorageType = 'localStorage' | 'sessionStorage';

export const storageUtils = {
	setItem: <T>(storage: StorageType, key: string, value: T): void => {
		const stringifiedValue = JSON.stringify(value);
		window[storage].setItem(key, stringifiedValue);
	},

	getItem: <T>(storage: StorageType, key: string, defaultValue: T): T => {
		const storedValue = window[storage].getItem(key);
		if (storedValue) {
			try {
				return JSON.parse(storedValue);
			} catch (e) {
				console.error(`Error parsing value from ${storage} for key "${key}":`, e);
				return defaultValue;
			}
		}
		return defaultValue;
	},

	removeItem: (storage: StorageType, key: string): void => {
		window[storage].removeItem(key);
	}
};

export const isOrganisationCheck = (groups: string[], user: User | undefined) => {
	if (groups?.includes(UserGroup.Admin)) return true;
	if (
		user?.roles?.length !== 0 &&
		user?.roles.map((r) => r.roleName === RoleName.BillingAdministrator).includes(true)
	)
		return false;
	if (
		(user?.roles?.length ?? 0) <= 1 &&
		user?.roles.map((r) => r.roleName === RoleName.Student || r.roleName === RoleName.Employee)
	)
		return false;
	return true;
};

export const supportedCurrencies = ['usd', 'gbp', 'eur', 'aed'];

export const StyledTourSwitch = styled(Switch)(({ theme }) => ({
	padding: 8,
	'& .MuiSwitch-track': {
		borderRadius: 22 / 2,
		'&::before, &::after': {
			content: '""',
			position: 'absolute',
			top: '50%',
			transform: 'translateY(-50%)',
			width: 16,
			height: 16
		},
		'&::before': {
			backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
				theme.palette.getContrastText(theme.palette.primary.main)
			)}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
			left: 12
		},
		'&::after': {
			backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
				theme.palette.getContrastText(theme.palette.primary.main)
			)}" d="M19,13H5V11H19V13Z" /></svg>')`,
			right: 12
		}
	},
	'& .MuiSwitch-thumb': {
		boxShadow: 'none',
		width: 16,
		height: 16,
		margin: 2
	}
}));
